/**
 * <s-product-thumbs> — PDP thumbnail strip.
 *
 * Clicking a thumbnail or progress dot scrolls the sibling gallery carousel to the matching slide
 * without moving the page, and marks the active thumbnail with aria-current.
 *
 * Required markup (inside the element):
 *   [data-product-gallery]          — horizontal scroll-snap carousel, one full-width slide per media.
 *   button[data-thumb-index="<n>"]  — thumbnail or dot; zero-based index of the slide to show.
 */
class SProductThumbs extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('click', this.#handleClick, opts);
    this.#gallery()?.addEventListener('scrollend', this.#syncActive, opts);
    // Fallback for browsers without scrollend: sync shortly after scrolling settles.
    this.#gallery()?.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });

    // Page opened on a specific variant: start on its photo.
    const initialMediaId = this.dataset.initialMediaId;
    if (initialMediaId) {
      requestAnimationFrame(() => this.showMedia(initialMediaId, { instant: true }));
    }
  }

  /**
   * Scrolls the gallery to the slide holding the given media id (used by <s-variant-picker>).
   * @param {string} mediaId
   * @param {{ instant?: boolean }} [options]
   */
  showMedia(mediaId, { instant = false } = {}) {
    const gallery = this.#gallery();
    if (!gallery || !mediaId) return;

    const slides = Array.from(gallery.querySelectorAll(':scope > [data-media-id]'));
    const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index < 0) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    gallery.scrollTo({ left: index * gallery.clientWidth, behavior: instant || reduceMotion ? 'auto' : 'smooth' });
    this.#setActive(index);
  }

  #scrollTimer;

  #handleScroll = () => {
    clearTimeout(this.#scrollTimer);
    this.#scrollTimer = setTimeout(this.#syncActive, 120);
  };

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #gallery() {
    return this.querySelector('[data-product-gallery]');
  }

  #handleClick = (event) => {
    const thumb = event.target.closest('[data-thumb-index]');
    const gallery = this.#gallery();
    if (!thumb || !gallery) return;

    const index = Number(thumb.dataset.thumbIndex);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    gallery.scrollTo({ left: index * gallery.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#setActive(index);
  };

  #syncActive = () => {
    const gallery = this.#gallery();
    if (!gallery || gallery.clientWidth === 0) return;
    this.#setActive(Math.round(gallery.scrollLeft / gallery.clientWidth));
  };

  #setActive(index) {
    for (const thumb of this.querySelectorAll('[data-thumb-index]')) {
      if (Number(thumb.dataset.thumbIndex) === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('s-product-thumbs')) {
  customElements.define('s-product-thumbs', SProductThumbs);
}
